import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { RootState } from '@store/types';
import { AirflowDashboardHandler } from '@api/airflow-dashboard-handler';
import { errorAlert, successAlert } from '@components/toastify/notify-toast';
import { AIRFLOW_BASE_PATH, DAG_RUN_DETAIL_TAB, DagRunDetailTab } from '../constants';
import { AirflowDagRun } from '../types';

type ConfirmActionType = 'success' | 'failed' | 'delete';

/*
 * Manages the DAG-run detail screen's header data, active tab, refresh propagation, and the
 * clear/mark-success/mark-failed/delete actions shared by the header across every tab
 */
export function useDagRunDetail() {
    // react-router-dom v5's useParams does not decode dynamic segments — dagId/runId arrive
    // exactly as they appeared in the URL (still percent-encoded, e.g. run IDs like
    // "scheduled__2026-08-13T06:45:00+00:00" become "...T06%3A45%3A00%2B00%3A00"), so encoding
    // them again for an API call would double-encode into "%253A" and 404. Decode once here.
    const { dagId: rawDagId, runId: rawRunId } = useParams<{ dagId: string; runId: string }>();
    const dagId = decodeURIComponent(rawDagId);
    const runId = decodeURIComponent(rawRunId);
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const history = useHistory();
    const location = useLocation();

    const [run, setRun] = useState<AirflowDagRun | null>(null);
    const [isLoadingRun, setIsLoadingRun] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);
    const [isClearing, setIsClearing] = useState(false);
    const [confirmAction, setConfirmAction] = useState<ConfirmActionType | null>(null);

    const activeTab = useMemo<DagRunDetailTab>(() => {
        const params = new URLSearchParams(location.search);
        return (params.get('tab') as DagRunDetailTab) || DAG_RUN_DETAIL_TAB.taskInstances;
    }, [location.search]);

    const handleTabChange = useCallback(
        (tab: string) => {
            const params = new URLSearchParams(location.search);
            params.set('tab', tab);
            history.replace({ pathname: location.pathname, search: params.toString() });
        },
        [history, location.pathname, location.search]
    );

    const fetchRun = useCallback(async () => {
        try {
            const response = await AirflowDashboardHandler.getDagRun(Env, dagId, runId);
            setRun(response);
        } catch {
            errorAlert(`Failed to load run "${runId}".`);
        } finally {
            setIsLoadingRun(false);
        }
    }, [Env, dagId, runId]);

    useEffect(() => {
        fetchRun();
    }, [dagId, runId]);

    /*
     * Re-fetches the run and bumps the refresh key every tab watches
     */
    const handleRefresh = useCallback(() => {
        fetchRun();
        setRefreshKey((key) => key + 1);
    }, [fetchRun]);

    /*
     * Opens the Clear DAG Run dialog
     */
    const handleClearRun = useCallback(() => setIsClearing(true), []);

    /*
     * Closes the Clear DAG Run dialog without refreshing (plain cancel)
     */
    const handleCloseClearDialog = useCallback(() => setIsClearing(false), []);

    /*
     * Refreshes the run + every tab after a successful clear
     */
    const handleRunCleared = useCallback(() => {
        handleRefresh();
    }, [handleRefresh]);

    /*
     * Executes the confirmed mark-success/mark-failed/delete action, navigating back to the
     * DAG detail screen on delete
     */
    const handleConfirmAction = useCallback(async () => {
        if (!confirmAction) return;
        try {
            if (confirmAction === 'delete') {
                await AirflowDashboardHandler.deleteDagRun(Env, dagId, runId);
                successAlert(`Deleted run "${runId}".`);
                history.push(`${AIRFLOW_BASE_PATH}/${encodeURIComponent(dagId)}`);
            } else {
                await AirflowDashboardHandler.patchDagRun(Env, dagId, runId, { state: confirmAction, note: null });
                successAlert(`Marked run "${runId}" as ${confirmAction}.`);
                handleRefresh();
            }
            setConfirmAction(null);
        } catch {
            errorAlert(`Failed to ${confirmAction} run "${runId}".`);
        }
    }, [Env, dagId, runId, confirmAction, history, handleRefresh]);

    /*
     * Navigates back to the DAG detail screen
     */
    const handleBack = useCallback(() => {
        history.push(`${AIRFLOW_BASE_PATH}/${encodeURIComponent(dagId)}`);
    }, [history, dagId]);

    /*
     * Navigates to the parent DAG's detail screen (used by the breadcrumb's DAG ID link)
     */
    const handleDagClick = useCallback(() => {
        history.push(`${AIRFLOW_BASE_PATH}/${encodeURIComponent(dagId)}`);
    }, [history, dagId]);

    return {
        dagId,
        runId,
        run,
        isLoadingRun,
        refreshKey,
        isClearing,
        confirmAction,
        setConfirmAction,
        activeTab,
        handleTabChange,
        handleRefresh,
        handleClearRun,
        handleCloseClearDialog,
        handleRunCleared,
        handleConfirmAction,
        handleBack,
        handleDagClick,
    };
}
